import React, { useEffect, useMemo, useState } from "react";
import {
  $Divider,
  $Button,
  $Input,
  $Select,
  $Radio,
  $Skeleton,
  $Message,
  $Tooltip,
  $Dropdown,
  $Menu,
} from "us.common/components";
import {
  PlusOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ReloadOutlined,
  DownOutlined,
  SortDescendingOutlined,
  SortAscendingOutlined,
} from "us.icons";
import {
  DocumentType,
  DEFAULT_TEMPLATE_DETAILS,
  DocumentTemplates,
  DEFAULT_SECTION_DATA,
} from "constants/TemplateEditor";
import {
  TemplateStatus,
  TemplateViewType,
  TemplateActions,
  URLParams,
  ITEM_OPTIONS,
  ItemType,
  TEXT_DISPLAY_CATEGORY,
  SORTING_OPTIONS,
  SORTING_ORDER,
  SortingOptions,
  SortingOrder,
} from "components/EditorHome/Constants";
import {
  getUrlParams,
  navigateToEditor,
} from "components/EditorHome/Functions";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import _, { remove } from "lodash";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import * as Actions from "actions";
import { useFormikContext } from "formik";
import { getFilteredTemplateData, groupByGroupId } from "functions";
import {
  getTemplateDocumentFormat,
  getTemplateGroupId,
  getTemplateMainAdapterId,
} from "components/HeaderExtra/Functions";
import { TextData } from "interfaces/TextEditor";
import { INITIAL_TEXT_OBJECT } from "constants/TextEditor";
import {
  getTemplateGroupName,
  getTemplateCategoryName,
  getDocumentFormatOptions,
} from "functions/EditorHome";
import { getSortedTemplateData, getOptionLabel } from "./Functions";
import { TemplateList, TextList } from "./Components";
import { UpdateMessageTemplates } from "components/EditorHome/Repository";
import { useLocalTemplate } from "hooks";

/**
 * @description - Template editor home list component.
 * @link Design Document -
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 11/09/2022
 */
const TemplateContent: React.FC<PropsFromRedux> = ({
  templates,
  documentTypes,
  templateCopy,
  setProperties,
  filterTemplates,
  copyTemplate,
  copyComplete,
  getTemplates,
  selectTemplate,
  setDrawerProperties,
  getAdaptersIds,
  resetParameters,
  sortTemplates,
  selectSection,
  resetTemplateVersions,
  resetTemplateRules,
  updateMessageTemplates,
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(search);

  const { values, setFieldValue } = useFormikContext<any>();
  const [templateView, setTemplateView] = useState(TemplateViewType.GRID);
  const [sortingCriteria, setSortingCriteria] = useState<string>(
    SortingOptions.LAST_UPDATED_DATE
  );
  const { saveLocalTemplate, getLocalTemplate, removeLocalTemplate } =
    useLocalTemplate();
  const [sortingOrder, setSortingOrder] = useState<string>(SortingOrder.DESC);

  const { initialValues, setValues } = useFormikContext();
  const { categoryId, groupId } = getUrlParams(search);

  const groupName = useMemo(() => {
    return getTemplateGroupName(templates.groups, groupId);
  }, [groupId, templates.groups]);

  const categoryName = useMemo(() => {
    return getTemplateCategoryName(templates.groups, groupId, categoryId);
  }, [groupName, categoryId, templates]);

  const isXML = categoryName == DocumentType.EHF;

  useEffect(() => {
    if (!templateCopy.loading && templateCopy.data && !templateCopy.completed) {
      handleNavigation(templateCopy.data);
      copyComplete && copyComplete({});
    }
  }, [templateCopy]);

  useEffect(() => {
    setFieldValue("itemType", ItemType.TEMPLATE);
  }, [categoryId]);

  const handleFilterValueChange = (values: any) => {
    const filteredTemplates = getFilteredTemplateData(templates.data, values);
    const sortedTemplates = getSortedTemplateData(
      filteredTemplates,
      sortingCriteria,
      sortingOrder
    );
    filterTemplates && filterTemplates(groupByGroupId(sortedTemplates));
    updateUrl(templates.data.length > sortedTemplates.length);
  };

  /**
   * @description - Update the browser URL according to the result
   * @param {boolean} value - Template group id
   */
  const updateUrl = (value: boolean) => {
  urlParams.set(URLParams.FILTERED, `${value}`);
  navigate({ search: urlParams.toString() }, { replace: true });
};

  /**
   * @description Handle template item action calls
   * @param {TemplateGridActions} actionType - template action type
   * @param {any} template - Selected template data
   */
  const onHandleTemplateAction = (
    actionType: TemplateActions,
    template: any
  ) => {
    switch (actionType) {
      case TemplateActions.COPY:
        handleCopyTemplate(template);
        break;
      case TemplateActions.ACTIVATE_OR_DEACTIVATE:
      case TemplateActions.DOWNLOAD:
        $Message.info(t("COMMON.THIS_FEATURE_IS_UNDER_DEVELOPMENT"));
        break;
      case TemplateActions.CREATE_NEW:
        handleCreateNewTemplate();
        break;
      case TemplateActions.EDIT:
        handleNavigation(template);
        break;
      case TemplateActions.ADD_TO_MAESSAGE_TEMPLATES:
        handleAddToMessageTemplates(template);
        break;
      default:
        break;
    }
  };

  const handleNavigation = (template: any) => {
    removeLocalTemplate();
    selectSection && selectSection(DEFAULT_SECTION_DATA);
    resetTemplateVersions && resetTemplateVersions({});
    if (template && template.templateId == 0) {
      // start with a new or copy of a template
      setProperties && setProperties(template);
    }
    selectTemplate && selectTemplate({ templateId: template.templateId });
    navigateToEditor(navigate, template);
  };

  const handleCopyTemplate = (template: any) => {
    removeLocalTemplate();
    resetTemplateRules && resetTemplateRules({});
    copyTemplate && copyTemplate({ templateId: template.templateId });
  };

  /**
   * @description open drawer with text editor view
   * @param {TextData} textObject - text data
   */

  const navigateToTextEditor = (textObject: TextData) => {
    setDrawerProperties &&
      setDrawerProperties({ visibility: true, content: textObject });
  };

  const handleCreateNewTemplate = () => {
    const groupId = getTemplateGroupId(templates.groups, groupName ?? "");
    const format = getTemplateDocumentFormat(categoryName, values.documentType);
    const defaultTemplate = DocumentTemplates[format];
    const mainAdapterId = getTemplateMainAdapterId(
      templates.groups,
      groupId,
      categoryId
    );
    const newTemplate = {
      ...defaultTemplate,
      groupId: Number(groupId),
      groupName: groupName,
      categoryId: Number(categoryId),
      outPutFormat: format,
      categoryName: categoryName,
      mainAdapterId: mainAdapterId,
      templateContent: {
        ...defaultTemplate.templateContent,
        output: {
          format,
        },
        group: groupName,
        categoryName: categoryName,
        adapter: {
          mainAdapterId: mainAdapterId,
          repeatingAdapters: [],
        },
      },
    };
    getAdaptersIds &&
      getAdaptersIds({
        categoryId: Number(categoryId),
        isRepeating: false,
        groupId: Number(groupId),
      });
    resetParameters && resetParameters({});
    removeLocalTemplate();
    handleNavigation(newTemplate);
  };

  /**
   * @description handle change of sorting option
   * @param {string} key - selected option
   */
  const handleSortingOptions = (key: string) => {
    setSortingCriteria(key);
    const filteredTemplates = getFilteredTemplateData(templates.data, values);
    const sortedTemplates = getSortedTemplateData(
      filteredTemplates,
      key,
      sortingOrder
    );
    sortTemplates && sortTemplates(sortedTemplates);
  };

  /**
   * @description handle change of sorting order
   */
  const handleSortingOrder = () => {
    const sortOrder =
      sortingOrder == SortingOrder.ASC
        ? SORTING_ORDER[1].value
        : SORTING_ORDER[0].value;
    setSortingOrder(sortOrder);
    const filteredTemplates = getFilteredTemplateData(templates.data, values);
    const sortedTemplates = getSortedTemplateData(
      filteredTemplates,
      sortingCriteria,
      sortOrder
    );
    sortTemplates && sortTemplates(sortedTemplates);
  };

  /**
   * @description handle refresh on user click
   */
  const handleRefresh = () => {
    setValues(initialValues);
    setSortingCriteria(SORTING_OPTIONS[0].value);
    setSortingOrder(SORTING_ORDER[1].value);
    getTemplates && getTemplates({});
  };

  /**
   * @description - Handle add/remove template from message templates
   * @param isAdded - Added or not status
   */
  const handleAddToMessageTemplates = (template: any) => {
    const params = UpdateMessageTemplates.call({
      templateId: template.templateId,
      isDelete: template.isMessageTemplateAdded,
    });
    // pass refreshAll boolean value to identify update all templates list or individual template details
    updateMessageTemplates &&
      updateMessageTemplates({ payload: params, refreshAll: true });
  };

  const SortMenu = (
    <$Menu onClick={({ key }) => handleSortingOptions(key)}>
      {SORTING_OPTIONS.map((option: any) => {
        return (
          <$Menu.Item key={option?.value}>
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="dropdownOptions"
            >
              {t(option?.label)}
            </a>
          </$Menu.Item>
        );
      })}
    </$Menu>
  );
  return (
    <>
      <div className="p-3 w-100">
        <div className="d-flex align-items-start justify-content-between">
          <div className="d-flex align-items-center">
            {!(values?.itemType == ItemType.TEXT) && (
              <div className="d-flex">
                <div className="mr-2">
                  <$Tooltip
                    placement="top"
                    title={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DOCUMENT_FORMAT"
                    )}
                  >
                    <$Select
                      name="documentType"
                      formitem={{}}
                      size="small"
                      style={{ width: 125 }}
                      loading={documentTypes.loading}
                      disabled={templates.loading || documentTypes.loading}
                      options={[
                        {
                          label: t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ALL_FORMATS"
                          ),
                          value: DocumentType.ALL,
                        },
                        ...getDocumentFormatOptions(documentTypes.data),
                      ]}
                      defaultValue={DocumentType.ALL}
                      allOption={false}
                      onChange={(value: any) => {
                        handleFilterValueChange({
                          ...(values as any),
                          documentType: value,
                        });
                      }}
                      hideSearch
                    />
                  </$Tooltip>
                </div>
                <div>
                  <$Tooltip
                    placement="top"
                    title={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DOCUMENT_STATUS"
                    )}
                  >
                    <$Select
                      dataTestid={"select-status"}
                      name="templateStatus"
                      formitem={{}}
                      size="small"
                      style={{ width: 125 }}
                      options={[
                        {
                          label: t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ALL_STATUS"
                          ),
                          value: TemplateStatus.ALL,
                        },
                        {
                          label: t("COMMON.ACTIVE"),
                          value: TemplateStatus.ACTIVE,
                        },
                        {
                          label: t("COMMON.INACTIVE"),
                          value: TemplateStatus.INACTIVE,
                        },
                      ]}
                      defaultValue={TemplateStatus.ALL}
                      onChange={(value: any) => {
                        handleFilterValueChange({
                          ...(values as any),
                          templateStatus: value,
                        });
                      }}
                      hideSearch
                      allOption={false}
                      disabled={templates.loading}
                    />
                  </$Tooltip>
                </div>
                <$Divider
                  className="mx-3"
                  style={{ marginTop: 10 }}
                  type="vertical"
                />
                <div className="mr-2">
                  <$Input
                    name="searchText"
                    placeholder={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.FILTER_BY_TEMPLATE_NAME"
                    )}
                    size="small"
                    style={{ width: 190 }}
                    allowClear
                    onChange={(e: any) => {
                      handleFilterValueChange({
                        ...(values as any),
                        searchText: e.target.value,
                      });
                      setFieldValue("searchText", e.target.value);
                    }}
                    disabled={templates.loading}
                  />
                </div>
                <div>
                  <$Input
                    name="activityNo"
                    placeholder={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.FILTER_BY_ACTIVITY_NO"
                    )}
                    size="small"
                    style={{ width: 190 }}
                    allowClear
                    onChange={(e: any) => {
                      handleFilterValueChange({
                        ...(values as any),
                        activityNo: e.target.value,
                      });
                      setFieldValue("activityNo", e.target.value);
                    }}
                    disabled={templates.loading}
                  />
                </div>
              </div>
            )}
          </div>

          <div
            className="d-flex justify-content-end align-items-center"
            style={{ marginTop: 4 }}
          >
            <div className="ml-4 mr-4">
              {t("US.COLLECTION.COMMON:COMMON.SORT_BY")} :
              <$Dropdown overlay={SortMenu} trigger={["click"]}>
                <a className="ant-dropdown-link" id="sortingCriteria">
                  {t(getOptionLabel(sortingCriteria, SORTING_OPTIONS))}
                  <DownOutlined />
                </a>
              </$Dropdown>
              <$Tooltip
                title={
                  sortingOrder == SortingOrder.ASC
                    ? t(SORTING_ORDER[1].label)
                    : t(SORTING_ORDER[0].label)
                }
              >
                {sortingOrder == SortingOrder.DESC ? (
                  <a className="pl-3" id="descending">
                    <SortDescendingOutlined
                      onClick={() => handleSortingOrder()}
                    />
                  </a>
                ) : (
                  <a className="pl-3" id="ascending">
                    <SortAscendingOutlined
                      onClick={() => handleSortingOrder()}
                    />
                  </a>
                )}
              </$Tooltip>
            </div>
            <$Tooltip placement="bottom" title={t("COMMON.REFRESH")}>
              <$Button
                icon={<ReloadOutlined />}
                type="default"
                size="small"
                onClick={() => handleRefresh()}
                disabled={templates.loading}
              />
            </$Tooltip>
          </div>
        </div>
        <$Divider className="mt-2 mb-1" />
        <$Skeleton
          loading={templates.loading}
          active
          paragraph={{
            rows: 3,
          }}
          className={templates.loading ? "mr-3 mt-5" : ""}
        >
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <span className="template-type-title mr-3">{categoryName}</span>
                <div className="d-flex align-items-center pt-1">
                  {String(categoryName).toLowerCase() ==
                    TEXT_DISPLAY_CATEGORY.toLowerCase() && (
                    <$Radio
                      name="itemType"
                      defaultValue={ItemType.TEMPLATE}
                      className="ml-3"
                      size="small"
                      options={ITEM_OPTIONS.map((option: any) => ({
                        ...option,
                        label: t(option.label),
                      }))}
                      radioButton="true"
                      buttonStyle="solid"
                      optionValue="value"
                      optionContent={(item: any, index: number) => item.label}
                    />
                  )}
                  {!isXML && (
                    <>
                      {values.itemType == ItemType.TEMPLATE ? (
                        <$Button
                          id="new-template-btn"
                          size="small"
                          icon={<PlusOutlined />}
                          className="new-template-btn ml-4"
                          onClick={() =>
                            onHandleTemplateAction(
                              TemplateActions.CREATE_NEW,
                              DEFAULT_TEMPLATE_DETAILS
                            )
                          }
                        >
                          {t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CREATE_NEW_TEMPLATE"
                          )}
                        </$Button>
                      ) : (
                        <$Button
                          id="new-template-btn"
                          size="small"
                          icon={<PlusOutlined />}
                          className="new-template-btn ml-4"
                          onClick={() =>
                            navigateToTextEditor(INITIAL_TEXT_OBJECT)
                          }
                        >
                          {t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CREATE_NEW_TEXT"
                          )}
                        </$Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-end align-items-center">
                <$Radio
                  name="tableView"
                  value={templateView}
                  className="m-0"
                  size="small"
                  options={[
                    {
                      label: "",
                      value: TemplateViewType.GRID,
                      icon: <AppstoreOutlined />,
                    },
                    {
                      label: "",
                      value: TemplateViewType.LIST,
                      icon: <UnorderedListOutlined />,
                    },
                  ]}
                  radioButton="true"
                  optionValue="value"
                  optionContent={(item: any, index: number) => item.icon}
                  onChange={(e: any) => {
                    setTemplateView(e.target.value);
                    e.target.value == TemplateViewType.LIST
                      ? document
                          .getElementById("new-template-btn")
                          ?.classList?.add("active")
                      : document
                          .getElementById("new-template-btn")
                          ?.classList?.remove("active");
                  }}
                  disabled={templates.loading}
                />
              </div>
            </div>

            <div className="template-list mt-3">
              {values?.itemType == ItemType.TEMPLATE ? (
                <TemplateList
                  templateView={templateView}
                  handleTemplateAction={onHandleTemplateAction}
                />
              ) : (
                <TextList
                  templateView={templateView}
                  navigateToTextEditor={navigateToTextEditor}
                />
              )}
            </div>
          </>
        </$Skeleton>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, editorHome } = state;
  const { templates, documentTypes, templateCopy } = templatesHistory;
  const { texts } = editorHome;
  return {
    templates,
    documentTypes,
    templateCopy,
    texts,
  };
};

const { pages, templates, templateCopy, template, templateVersions } =
  Actions.templatesHistory;
const { parameters, repeatingAdapters, section } = Actions.templateEditor;
const { editorHome, textEditor } = Actions;
const { templateRules, settings, messageTemplates } = editorHome;
const { drawer } = textEditor;

const mapDispatchToProps = {
  setProperties: pages.setProperties,
  filterTemplates: templates.filter,
  copyTemplate: templateCopy.get,
  copyComplete: templateCopy.complete,
  getParameters: parameters.get,
  getTemplates: templates.get,
  selectTemplate: template.select,
  getAllTemplateRules: templateRules.get,
  setDrawerProperties: drawer.set,
  getSettings: settings.get,
  getAdaptersIds: repeatingAdapters.get,
  resetParameters: parameters.reset,
  sortTemplates: templates.sort,
  selectSection: section.selectSection,
  resetTemplateVersions: templateVersions.reset,
  resetTemplateRules: templateRules.reset,
  updateMessageTemplates: messageTemplates.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TemplateContent);
